// The aim is stored relative to the clicked element: the commit and the static re-place move the screen point in between.
import type { Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection, type Selection, TextSelection } from '@tiptap/pm/state';
import { cellAround, CellSelection } from '@tiptap/pm/tables';

import { ELEMENT_ID_ATTRS, STATIC_ELEMENT_ID_ATTR } from '../constants';
import { currentEditor } from './liveEditor';
import { cellUnder, trace } from '../trace';

export type TPoint = { x: number; y: number };

/** Where the click landed inside the element, and where that element was when it did. */
type TAim = { point: TPoint; elementId: string | null; elementLeft: number; elementTop: number };

let pendingCaret: TAim | null = null;
let pendingCaretEnd: TAim | null = null;

// The editor's copy first: a plain lookup finds the static half, which holds the same id.
const aimedElement = (elementId: string): HTMLElement | null => {
  // The element's own id attributes, plus the id the static half stamps on what it places.
  const selector = [...ELEMENT_ID_ATTRS, STATIC_ELEMENT_ID_ATTR]
    .map((attr) => `[${attr}="${CSS.escape(elementId)}"]`)
    .join(',');
  return (
    document.querySelector<HTMLElement>(`.ProseMirror ${selector}`) ?? document.querySelector<HTMLElement>(selector)
  );
};

const aimAt = (point: TPoint | null, elementId: string | null): TAim | null => {
  if (!point) return null;
  const box = elementId ? aimedElement(elementId)?.getBoundingClientRect() : null;
  return { point, elementId, elementLeft: box?.left ?? point.x, elementTop: box?.top ?? point.y };
};

const aimNow = (aim: TAim): TPoint => {
  const box = aim.elementId ? aimedElement(aim.elementId)?.getBoundingClientRect() : null;
  if (!box) return aim.point;
  return { x: aim.point.x + (box.left - aim.elementLeft), y: aim.point.y + (box.top - aim.elementTop) };
};

export const aimCaretAt = (point: TPoint | null, end: TPoint | null, elementId?: string | null) => {
  pendingCaret = aimAt(point, elementId ?? null);
  pendingCaretEnd = aimAt(end, elementId ?? null);
};

export const clearPendingCaret = () => aimCaretAt(null, null, null);

/** `table` identifies the table only for comparing two cells - `cellAround` resolves before the cell. */
const cellAt = (doc: PMNode, pos: number): { cell: number; table: number } | null => {
  const $cell = cellAround(doc.resolve(pos));
  return $cell ? { cell: $cell.pos, table: $cell.start(-1) } : null;
};

// `TextSelection.near` searches FORWARD by default, which from the end of a cell is the next cell.
export const caretIn = (doc: PMNode, pos: number): Selection => {
  const forward = TextSelection.near(doc.resolve(pos), 1);
  const aimed = cellAt(doc, pos);
  if (!aimed || cellAt(doc, forward.from)?.cell === aimed.cell) return forward;
  const backward = TextSelection.near(doc.resolve(pos), -1);
  return cellAt(doc, backward.from)?.cell === aimed.cell ? backward : forward;
};

/**
 * `posAtCoords().inside` is the node the pointer was over; -1 when it was over plain text.
 * Selectable leaves only: a paragraph is also "inside" and must stay a caret.
 */
const selectableLeafAt = (doc: PMNode, inside: number): number => {
  if (inside < 0 || inside > doc.content.size) return -1;
  const node = doc.nodeAt(inside);
  const selectable = node && node.isLeaf && node.type.spec.selectable !== false;
  return selectable ? inside : -1;
};

/**
 * How many frames the replay may wait for the editor to answer.
 *
 * Node views are still mounting, so a point over a cell can resolve to nothing at first - and a point
 * kept forever would place the caret during some later, unrelated swap.
 */
const PLACE_ATTEMPTS = 4;

const describe = (editor: ReturnType<typeof currentEditor>, pos: number): Record<string, unknown> => {
  if (!editor) return { noEditor: true };
  try {
    const dom = editor.view.domAtPos(pos).node;
    const element = dom.nodeType === 3 ? dom.parentElement : (dom as HTMLElement);
    const cell = element?.closest('td,th') ?? null;
    const row = cell?.parentElement ?? null;
    const owner = element?.closest('[data-id]') ?? null;
    return {
      pos,
      cell: cell && row ? [...row.children].indexOf(cell) : null,
      row: row?.parentElement ? [...row.parentElement.children].indexOf(row) : null,
      cellText: (cell?.textContent ?? '').trim().slice(0, 24) || null,
      elementId: owner?.getAttribute('data-id') ?? null,
      rowWidths: row ? [...row.children].map((c) => Math.round(c.getBoundingClientRect().width)) : null,
    };
  } catch (error) {
    return { pos, unresolved: String(error).slice(0, 60) };
  }
};

export const placeCaretAtPendingClick = (attempt = 0) => {
  const point = pendingCaret;
  const endPoint = pendingCaretEnd;
  const editor = currentEditor();
  const again = () => {
    if (attempt + 1 >= PLACE_ATTEMPTS) return clearPendingCaret();
    requestAnimationFrame(() => {
      // A click made while this retry waited owns the aim now - retrying would resolve it against the old click's part.
      if (pendingCaret === point) placeCaretAtPendingClick(attempt + 1);
    });
  };
  if (!point) return;
  // Not a wait-and-see state: read-only stays read-only until the user leaves the mode.
  if (!editor || !editor.isEditable) {
    clearPendingCaret();
    trace('caret:skipped', { reason: editor ? 'not editable' : 'no editor' });
    return;
  }
  const aimed = aimNow(point);
  const at = editor.view.posAtCoords({ left: aimed.x, top: aimed.y });
  if (!at) {
    trace('caret:no-position', { point: aimed, attempt });
    return again();
  }
  clearPendingCaret();
  const under = document.elementFromPoint(aimed.x, aimed.y);
  trace('caret:placed', {
    point: aimed,
    aimedAt: point.point,
    movedBy: { x: Math.round(aimed.x - point.point.x), y: Math.round(aimed.y - point.point.y) },
    pos: at.pos,
    aimId: point.elementId,
    underId: under?.closest('[data-id]')?.getAttribute('data-id') ?? under?.closest('[id]')?.id ?? null,
    underIsEditor: Boolean(under && editor.view.dom.contains(under)),
    // The same shape the click recorded from the static copy. If these two disagree, the point was
    // over one cell when the user clicked and over another by the time the editor answered.
    landedOn: cellUnder(aimed.x, aimed.y),
  });

  const { state } = editor.view;
  const clamp = (value: number) => Math.max(0, Math.min(value, state.doc.content.size));
  const from = clamp(at.pos);
  // The other end can miss - the user may have dragged past the part - and the caret alone still beats nothing.
  const endAimed = endPoint ? aimNow(endPoint) : null;
  const end = endAimed ? editor.view.posAtCoords({ left: endAimed.x, top: endAimed.y }) : null;
  const to = end ? clamp(end.pos) : from;
  /**
   * The KIND of selection has to match what was aimed at, not just the position.
   *
   * Every command that acts on cells reads `CellSelection`, and every one that acts on an image or
   * another leaf reads `NodeSelection`; handed a text selection they do nothing, and the bubble menu
   * for that thing never opens. `between` rather than `create` for the plain case - `create` throws
   * when an endpoint is not in inline content, which a drag across a table produces.
   */
  const startCell = to !== from ? cellAt(state.doc, from) : null;
  const endCell = startCell ? cellAt(state.doc, to) : null;
  const leaf = to === from ? selectableLeafAt(state.doc, at.inside) : -1;
  const selection =
    startCell && endCell && startCell.table === endCell.table && startCell.cell !== endCell.cell
      ? CellSelection.create(state.doc, startCell.cell, endCell.cell)
      : leaf >= 0
        ? NodeSelection.create(state.doc, leaf)
        : to !== from
          ? TextSelection.between(state.doc.resolve(from), state.doc.resolve(to))
          : caretIn(state.doc, from);
  if (endPoint) trace('selection:restored', { from, to, resolvedEnd: Boolean(end) });
  trace('caret:selection', {
    kind: selection.constructor.name,
    from,
    to,
    askedFor: from,
    settledOn: selection.from,
    movedIntoNext: selection.from !== from ? describe(editor, selection.from) : null,
    resolved: describe(editor, from),
  });
  editor.view.dispatch(state.tr.setSelection(selection));
  const placedAt = from;
  const watchUntil = performance.now() + 2500;
  const onTransaction = ({
    transaction,
  }: {
    transaction: { selectionSet: boolean; docChanged: boolean; getMeta: (key: string) => unknown; steps: unknown[] };
  }) => {
    const now = currentEditor();
    if (!now) return;
    if (performance.now() > watchUntil) {
      now.off('transaction', onTransaction);
      return;
    }
    if (!transaction.selectionSet && !transaction.docChanged) return;
    const nowFrom = now.state.selection.from;
    if (nowFrom === placedAt) return;
    trace('caret:MOVED-AFTER-PLACEMENT', {
      placedAt,
      nowAt: nowFrom,
      selectionSet: transaction.selectionSet,
      docChanged: transaction.docChanged,
      steps: transaction.steps.length,
      pointer: Boolean(transaction.getMeta('pointer')),
      uiEvent: transaction.getMeta('uiEvent') ?? null,
      addToHistory: transaction.getMeta('addToHistory') ?? null,
      was: describe(now, placedAt),
      now: describe(now, nowFrom),
    });
    now.off('transaction', onTransaction);
  };
  editor.on('transaction', onTransaction);
  // `view.focus()`, not `dom.focus()`: it writes the state's selection into the DOM, so a later DOM change cannot read a stale one back over it.
  editor.view.focus();
};
